/**
 * Coverage AutoFill (CAR) — CAR-6 aggregator.
 *
 * Per-requirement coverage-fill transforms (CAR-1..CAR-5) each produce
 * a typed `<RequirementName>CoverageFill` payload. CAR-6 collects them
 * into a single `RequirementAutoFill` registry keyed by checklist
 * `ChecklistItem.id` so downstream consumers (the AuthorEvidenceDialog
 * prefill, RDX `inputResolution.priorAutoFill`, report-prompt
 * `[Ref AF-N]` references) can read a single uniform shape.
 *
 * Constraints:
 *   - Pack §CAR-6 originally mapped to `sed.slots[*]` directly. That
 *     surface (CAF's `RequirementCoverage`) isn't shipped yet — the
 *     stub `PROVIDER_REQUIREMENT_MAP` referenced in the pack lives in
 *     a queued CAF pack (0.99249). Until CAF lands, CAR exposes the
 *     auto-fill registry as a standalone bundle that callers can plug
 *     into the existing AuthorEvidenceDialog prefill path
 *     (`AuthorEvidenceDialog.prefill.test.tsx` already exists and
 *     reads `requirementId`-keyed prefill payloads).
 *   - Composes additively: callers pass only the providers they have.
 *     A site with FEMA data but no HIFLD gets a flood-only auto-fill.
 *
 * Hallucination guard: every entry in the returned map carries
 * `sourceUrl` + `sourceProvider`. Empty inputs → empty map (no
 * anonymous entries).
 */
import type { FloodData } from '../../services/dataProviders/femaFlood';
import type { GridData } from '../../types/siteRag';
import type { GeotechnicalData } from '../../services/dataProviders/nrcsSoilSurvey';
import type {
  FloodEvidence,
  GeotechnicalEvidence,
  GridEvidence,
  SiteEvidenceDocument,
  StructuredEvidenceValue,
} from '../../types/siteEvidence';
import {
  mapFloodDataToCoverage,
  type FloodCoverageFill,
  FLOOD_CERT_FIELD_KEYS,
} from './floodCoverageFill';
import {
  mapGridDataToCoverage,
  type UtilityCoverageFill,
  UTILITY_LETTER_FIELD_KEYS,
} from './utilityCoverageFill';
import {
  mapGeotechnicalDataToCoverage,
  type GeotechCoverageFill,
  GEOTECH_FIELD_KEYS,
} from './geotechCoverageFill';
import {
  allChecklistItemsWithSchema,
  getAlignmentForRequirement,
} from './requirementFieldAlignment';
import { canPopulateFromTier } from './populateSourcePolicy';
import { extractFieldValue } from '../evidenceLineage/requirementToFields';
import { btmSedToRequirementAutoFill } from './btmCoverageFill';
import { supplyChainPlanToRequirementAutoFill } from './supplyChainCoverageFill';
import { connectivitySedToRequirementAutoFill } from './connectivityCoverageFill';
import { publicSentimentSedToRequirementAutoFill } from './publicSentimentCoverageFill';
import { zoningSedToRequirementAutoFill } from './zoningCoverageFill';
import { environmentalPrefsToRequirementAutoFill } from './environmentalPrefsCoverageFill';
import { typedPreferencesToRequirementAutoFill } from './typedPreferencesCoverageFill';
import type { SupplyChainPlan } from '../../types/supplyChain';
import type { EnvironmentalPreferences, ProjectIntent, TypedPreferencesByType } from '../../types/siteRag';
import type { SiteContext } from '../../types/siteContext';
import type { SiteEntity } from '../../types/site';
import type { ReportTypeId } from '../../config/reportTypes';

/**
 * Per-requirement auto-fill envelope. Mirrors the shape the
 * AuthorEvidenceDialog already consumes for user-typed prefill
 * (`{ fields, notes, confidence }`) plus the CAR provenance stamp
 * (`sourceProvider`, `sourceUrl`) the Coverage Matrix lineage chip
 * needs to render the `🛰` (provider) indicator.
 */
export interface RequirementAutoFill {
  /** Stable id matching `ChecklistItem.id` (`'flood-cert'`,
   *  `'utility-letter'`, …). */
  requirementId: string;
  /** Per-field values for the AuthoredEvidenceFormState. */
  fields: Record<string, string | number>;
  /** Notes text prefilled into the dialog's `<textarea>`. */
  notes: string;
  /** Confidence band — drives the dialog's tier dropdown default. */
  confidence: { tier: 'low' | 'medium' | 'high'; value: 0.45 | 0.6 | 0.75; rationale: string };
  /** Provider id (`'fema-nfhl'`, `'hifld-grid'`, …). */
  sourceProvider: string;
  /** Public URL for the source. */
  sourceUrl: string;
  /** How many of the schema's structured fields are filled. */
  fieldsFilledCount: number;
  /** Total schema fields. */
  fieldsTotalCount: number;
}

/**
 * Input bundle: any subset of provider responses the caller has.
 * Each is optional; missing inputs produce no registry entry.
 */
export interface CompileCoverageAutoFillInput {
  /** FEMA NFHL response (drives the `flood-cert` requirement). */
  flood?: FloodData | null;
  /** HIFLD substation + transmission response (drives the
   *  `utility-letter` requirement). */
  grid?: GridData | null;
  /** NRCS SSURGO response (drives the `geotech` requirement — CAR-1). */
  geotechnical?: GeotechnicalData | null;
}

/**
 * Auto-fill registry. Keys are `ChecklistItem.id`. Empty when the
 * caller passed nothing. Iterable for downstream UI.
 */
export type RequirementAutoFillRegistry = Record<string, RequirementAutoFill>;

/**
 * Compile available provider data into a per-requirement auto-fill
 * registry. Pure — no async, no HTTP, no Gemini.
 *
 * @param input  Subset of provider responses the caller has on hand.
 * @returns      Registry keyed by `ChecklistItem.id`. Empty when no
 *               input.
 */
export function compileCoverageAutoFill(
  input: CompileCoverageAutoFillInput,
): RequirementAutoFillRegistry {
  const registry: RequirementAutoFillRegistry = {};

  if (input.flood) {
    const floodFill = mapFloodDataToCoverage(input.flood);
    registry['flood-cert'] = floodFillToRequirement('flood-cert', floodFill);
  }

  if (input.grid) {
    const utilityFill = mapGridDataToCoverage(input.grid);
    if (utilityFill.fieldsFilledCount > 0) {
      registry['utility-letter'] = utilityFillToRequirement('utility-letter', utilityFill);
    }
  }

  if (input.geotechnical) {
    const geotechFill = mapGeotechnicalDataToCoverage(input.geotechnical);
    registry['geotech'] = geotechFillToRequirement('geotech', geotechFill);
  }

  return registry;
}

/**
 * Build a CAR auto-fill registry from an existing SED. Reconstructs
 * provider-shaped inputs from populated slot `data` and merges any
 * `provenanceDetail.autoFillFields` stamped during bootstrap (CAR-6).
 *
 * Used by Coverage Matrix "Populate from available data" when the host
 * does not pass a pre-built registry.
 */
export function compileCoverageAutoFillFromSed(
  sed: SiteEvidenceDocument | undefined,
  options?: {
    supplyChainPlan?: SupplyChainPlan | null;
    /** @deprecated use typedPreferences */
    environmentalPreferences?: EnvironmentalPreferences | null;
    typedPreferences?: TypedPreferencesByType | null;
    projectIntent?: ProjectIntent | null;
    siteContext?: SiteContext | null;
    site?: SiteEntity | null;
    reportTypes?: readonly ReportTypeId[];
  },
): RequirementAutoFillRegistry {
  const envFromTyped = options?.typedPreferences?.environmental ?? options?.environmentalPreferences;

  if (!sed) {
    return mergeTypedPreferencesAutoFill(
      mergeEnvironmentalPrefsAutoFill(
        mergeSupplyChainAutoFill({}, options?.supplyChainPlan),
        envFromTyped,
      ),
      options,
    );
  }

  const input: CompileCoverageAutoFillInput = {};

  const floodSlot = sed.flood;
  if (floodSlot && floodSlot.status !== 'unavailable' && floodSlot.data?.floodZone) {
    const d = floodSlot.data as FloodEvidence;
    input.flood = {
      femaZone: d.floodZone,
      isSpecialFloodHazard: d.sfha,
      floodRisk: d.floodRisk,
      zoneOverlap: d.zoneOverlap,
      sampledPoints: d.sampledPoints,
      // Disclosure-bearing fields travel with the rebuilt record so the
      // AutoFill note can name a geometry fallback and the coverage state.
      // The SED union carries one extra member ('sample-grid-estimate') the
      // provider shape does not; it is a sweep for note purposes, so it is
      // simply not carried. The fallback disclosure keys on areaAnalysis.
      ...(d.analysisMethod && d.analysisMethod !== 'sample-grid-estimate' ? { analysisMethod: d.analysisMethod } : {}),
      ...(d.areaAnalysis ? { areaAnalysis: d.areaAnalysis as NonNullable<CompileCoverageAutoFillInput['flood']>['areaAnalysis'] } : {}),
      ...(d.screeningCoverage ? { screeningCoverage: d.screeningCoverage } : {}),
    };
  }

  const gridSlot = sed.grid;
  if (gridSlot && gridSlot.status !== 'unavailable' && gridSlot.data) {
    input.grid = gridEvidenceToGridData(gridSlot.data as GridEvidence);
  }

  const geotechnicalSlot = sed.geotechnical;
  if (
    geotechnicalSlot &&
    geotechnicalSlot.status !== 'unavailable' &&
    geotechnicalSlot.data
  ) {
    const d = geotechnicalSlot.data as GeotechnicalEvidence;
    const screen = d.publicScreen;
    if (screen?.mukey) {
      input.geotechnical = {
        mukey: screen.mukey,
        mapUnitName: screen.mapUnitName ?? null,
        uscsClass: (screen.inferredUsCsClass as GeotechnicalData['uscsClass']) ?? null,
        usdaTexture: screen.usdaTexture ?? null,
        drainageClass: screen.drainageClass ?? null,
        depthToBedrockCm:
          screen.depthToBedrockM == null ? null : screen.depthToBedrockM * 100,
        depthToWaterTableCm:
          screen.seasonalWaterTableDepthM == null
            ? null
            : screen.seasonalWaterTableDepthM * 100,
      };
    }
  }

  const registry = compileCoverageAutoFill(input);

  mergeStoredAutoFillFields(
    registry,
    'geotech',
    sed.geotechnical?.provenanceDetail?.autoFillFields ?? sed.seismic?.provenanceDetail?.autoFillFields,
    sed.geotechnical?.provenanceDetail?.autoFillProvider ?? sed.seismic?.provenanceDetail?.autoFillProvider ?? 'nrcs-soil-survey',
    GEOTECH_FIELD_KEYS,
  );
  mergeStoredAutoFillFields(
    registry,
    'utility-letter',
    utilityLetterStoredFields(sed),
    sed.grid?.provenanceDetail?.autoFillProvider ?? 'user-doc',
    UTILITY_LETTER_FIELD_KEYS,
  );
  mergeStoredAutoFillFields(
    registry,
    'flood-cert',
    sed.flood?.provenanceDetail?.autoFillFields,
    sed.flood?.provenanceDetail?.autoFillProvider ?? 'fema-nfhl',
    FLOOD_CERT_FIELD_KEYS,
  );
  mergeStoredAutoFillFields(
    registry,
    'parcel-survey',
    parcelSurveyStoredFields(sed),
    sed.parcel?.provenanceDetail?.autoFillProvider ?? 'regrid-title',
    ['parcelId', 'recordedAcreage', 'netUsableAcres', 'deedRestrictions', 'easements'],
  );
  mergeStoredAutoFillFields(
    registry,
    'zoning',
    sed.parcel?.provenanceDetail?.autoFillFields,
    sed.parcel?.provenanceDetail?.autoFillProvider ?? 'county-zoning',
    ['zoningCode', 'permittedUses', 'setbacks_ft', 'heightLimit_ft', 'specialConditions'],
  );
  mergeStoredAutoFillFields(
    registry,
    'utility-locate',
    sed.parcel?.provenanceDetail?.autoFillFields,
    sed.parcel?.provenanceDetail?.autoFillProvider ?? 'user-doc',
    ['locator', 'locateDate', 'utilitiesLocated', 'encumbrances', 'setbackImpact_ft'],
  );
  mergeStoredAutoFillFields(
    registry,
    'survey',
    sed.parcel?.provenanceDetail?.autoFillFields,
    sed.parcel?.provenanceDetail?.autoFillProvider ?? 'user-doc',
    ['surveyor', 'surveyDate', 'surveyedAcreage', 'avgSlope_pct', 'verticalDatum'],
  );

  mergeSedAlignmentAutoFill(registry, sed);

  const btmFill = btmSedToRequirementAutoFill(sed);
  if (btmFill) registry['btm-vendor-quote'] = btmFill;

  const fiberFill = connectivitySedToRequirementAutoFill(sed);
  if (fiberFill) registry['fiber-survey'] = fiberFill;

  const sentimentFill = publicSentimentSedToRequirementAutoFill(sed);
  if (sentimentFill) registry['community-feedback-summary'] = sentimentFill;

  const zoningFill = zoningSedToRequirementAutoFill(sed);
  if (zoningFill) {
    const existing = registry['zoning'];
    registry['zoning'] = existing
      ? {
          ...existing,
          fields: { ...zoningFill.fields, ...existing.fields },
          notes: existing.notes || zoningFill.notes,
          fieldsFilledCount: Object.keys({ ...zoningFill.fields, ...existing.fields }).length,
        }
      : zoningFill;
  }

  return mergeTypedPreferencesAutoFill(
    mergeEnvironmentalPrefsAutoFill(
      mergeSupplyChainAutoFill(registry, options?.supplyChainPlan),
      envFromTyped,
    ),
    options,
  );
}

function mergeTypedPreferencesAutoFill(
  registry: RequirementAutoFillRegistry,
  options?: {
    typedPreferences?: TypedPreferencesByType | null;
    projectIntent?: ProjectIntent | null;
    siteContext?: SiteContext | null;
    site?: SiteEntity | null;
    reportTypes?: readonly ReportTypeId[];
  },
): RequirementAutoFillRegistry {
  const typedFill = typedPreferencesToRequirementAutoFill({
    typedPreferences: options?.typedPreferences,
    projectIntent: options?.projectIntent,
    siteContext: options?.siteContext,
    site: options?.site,
    reportTypes: options?.reportTypes,
  });
  return { ...typedFill, ...registry };
}

function mergeEnvironmentalPrefsAutoFill(
  registry: RequirementAutoFillRegistry,
  prefs?: EnvironmentalPreferences | null,
): RequirementAutoFillRegistry {
  const envFill = environmentalPrefsToRequirementAutoFill(prefs);
  for (const [reqId, fill] of Object.entries(envFill)) {
    const existing = registry[reqId];
    registry[reqId] = existing
      ? {
          ...existing,
          fields: { ...fill.fields, ...existing.fields },
          notes: [fill.notes, existing.notes].filter(Boolean).join(' '),
          fieldsFilledCount: Object.keys({ ...fill.fields, ...existing.fields }).length,
          fieldsTotalCount: Math.max(fill.fieldsTotalCount, existing.fieldsTotalCount),
        }
      : fill;
  }
  return registry;
}

function mergeSupplyChainAutoFill(
  registry: RequirementAutoFillRegistry,
  plan?: SupplyChainPlan | null,
): RequirementAutoFillRegistry {
  if (!plan || plan.bom.length === 0) return registry;
  registry['vendor-quote-pack'] = supplyChainPlanToRequirementAutoFill(plan);
  return registry;
}

/**
 * Fill requirements from aligned SED field paths (alias / direct).
 * Skips doc-only and transform keys — transforms use CAR mappers.
 */
export function mergeSedAlignmentAutoFill(
  registry: RequirementAutoFillRegistry,
  sed: SiteEvidenceDocument,
): void {
  for (const item of allChecklistItemsWithSchema()) {
    const alignment = getAlignmentForRequirement(item.id);
    const fields: Record<string, string | number> = {};
    let total = 0;
    for (const row of alignment) {
      if (row.kind === 'doc-only' || row.kind === 'transform' || !row.fieldPath) continue;
      if (!canPopulateFromTier(item.id, row.schemaKey, 'sed')) continue;
      total += 1;
      const resolved = extractFieldValue(sed, row.fieldPath);
      if (resolved.isEmpty) continue;
      const v = resolved.value;
      if (typeof v === 'string' || typeof v === 'number') {
        fields[row.schemaKey] = v;
      } else if (typeof v === 'boolean') {
        fields[row.schemaKey] = v ? 1 : 0;
      } else if (Array.isArray(v)) {
        fields[row.schemaKey] = v.filter(x => typeof x === 'string').join(', ');
      }
    }
    if (Object.keys(fields).length === 0) continue;
    const existing = registry[item.id];
    if (existing) {
      registry[item.id] = {
        ...existing,
        fields: { ...existing.fields, ...fields },
        fieldsFilledCount: Object.keys({ ...existing.fields, ...fields }).length,
      };
    } else {
      registry[item.id] = {
        requirementId: item.id,
        fields,
        notes: 'Auto-filled from aligned SED fields — review before relying on it.',
        confidence: { tier: 'medium', value: 0.6, rationale: 'SED alignment auto-fill' },
        sourceProvider: 'sed-alignment',
        sourceUrl: '',
        fieldsFilledCount: Object.keys(fields).length,
        fieldsTotalCount: total || Object.keys(fields).length,
      };
    }
  }
}

function gridEvidenceToGridData(ev: GridEvidence): GridData {
  return {
    substationDist: ev.substationDistKm,
    capacity: ev.capacity,
    complexity: ev.complexity,
    voltage: ev.voltage,
    nearestSubstationName: ev.substationName,
    substationsWithin50km: ev.substationsWithin50km,
    transmissionLines: ev.transmissionLines,
    nearestTransmissionKm: ev.nearestTransmissionKm,
    nearestTransmissionVoltage: ev.nearestTransmissionVoltage,
    estimatedInterconnectionCost: ev.estimatedInterconnectionCost,
    estimatedInterconnectionCostBasis: ev.estimatedInterconnectionCostBasis,
    estimatedQueueTime: ev.estimatedQueueTime,
    estimatedQueueTimeBasis: ev.estimatedQueueTimeBasis,
    isoRegion: ev.isoRegion,
  };
}

/** Net usable area has its own document receipt; gross parcel/provider area
 * cannot populate this new doc-only quantity under a renamed key. */
function parcelSurveyStoredFields(sed: SiteEvidenceDocument): Record<string, StructuredEvidenceValue> | undefined {
  const detail = sed.parcel?.provenanceDetail;
  if (!detail?.autoFillFields) return undefined;
  const fields = { ...detail.autoFillFields };
  const source = detail.autoFillFieldSources?.netUsableAcres;
  if (!source?.docId || source.requirementId !== 'parcel-survey'
    || JSON.stringify(source.value) !== JSON.stringify(fields.netUsableAcres)) {
    delete fields.netUsableAcres;
  }
  return fields;
}

/** Reject cached map heuristics, including fields mixed into a later document stamp. */
function utilityLetterStoredFields(sed: SiteEvidenceDocument): Record<string, StructuredEvidenceValue> | undefined {
  const slot = sed.grid;
  const detail = slot?.provenanceDetail;
  if (!detail?.autoFillFields) return undefined;
  if (detail.autoFillFieldSources) {
    return Object.fromEntries(Object.entries(detail.autoFillFields).filter(([key, value]) => {
      const source = detail.autoFillFieldSources?.[key];
      return source?.docId && ['utility-letter', 'utility-service-letter'].includes(source.requirementId)
        && JSON.stringify(source.value) === JSON.stringify(value);
    }));
  }
  // Preserve legacy document-backed fields; provider-only/no-source caches
  // cannot turn estimated capacity or costs into user-authoritative values.
  return detail.autoFillProvider === 'user-doc'
    && (slot.status === 'verified-from-doc' || slot.status === 'verified-from-context')
    ? detail.autoFillFields : undefined;
}

function mergeStoredAutoFillFields(
  registry: RequirementAutoFillRegistry,
  requirementId: string,
  stored: Record<string, StructuredEvidenceValue> | undefined,
  sourceProvider: string,
  allowedKeys: readonly string[],
): void {
  if (!stored || Object.keys(stored).length === 0) return;
  const fields = stripUndefined(
    Object.fromEntries(
      allowedKeys
        .filter(k => stored[k] !== undefined && stored[k] !== null)
        .map(k => {
          const value = stored[k];
          return [k, Array.isArray(value) ? value.join(', ') : value] as const;
        }),
    ),
  );
  if (Object.keys(fields).length === 0) return;
  const existing = registry[requirementId];
  if (existing) {
    registry[requirementId] = {
      ...existing,
      fields: { ...existing.fields, ...fields },
      fieldsFilledCount: Object.keys({ ...existing.fields, ...fields }).length,
    };
    return;
  }
  registry[requirementId] = {
    requirementId,
    fields,
    notes: 'Auto-filled from SED provider data — review before relying on it.',
    confidence: { tier: 'medium', value: 0.6, rationale: 'SED auto-fill fields' },
    sourceProvider,
    sourceUrl: '',
    fieldsFilledCount: Object.keys(fields).length,
    fieldsTotalCount: allowedKeys.length,
  };
}

/** Number of requirements the auto-fill registry currently covers.
 *  Useful for telemetry / Coverage Matrix headlines. Increments as
 *  CAR-1 / CAR-3 / CAR-4 land. */
export function autoFillCoverageCount(registry: RequirementAutoFillRegistry): {
  filled: number;
  total: number;
} {
  // CAR currently wires 5+ Coverage Matrix requirements via provider
  // transforms + SED alignment. Total reflects design target.
  const TARGET_REQUIREMENTS = 12;
  return { filled: Object.keys(registry).length, total: TARGET_REQUIREMENTS };
}

// ── Narrowing helpers: per-requirement fill → registry entry. ──────────

function floodFillToRequirement(reqId: string, fill: FloodCoverageFill): RequirementAutoFill {
  return {
    requirementId: reqId,
    fields: stripUndefined(fill.fields),
    notes: fill.notes,
    confidence: fill.confidence,
    sourceProvider: fill.sourceProvider,
    sourceUrl: fill.sourceUrl,
    fieldsFilledCount: fill.fieldsFilledCount,
    fieldsTotalCount: fill.fieldsTotalCount,
  };
}

function utilityFillToRequirement(reqId: string, fill: UtilityCoverageFill): RequirementAutoFill {
  return {
    requirementId: reqId,
    fields: stripUndefined(fill.fields),
    notes: fill.notes,
    confidence: fill.confidence,
    sourceProvider: fill.sourceProvider,
    sourceUrl: fill.sourceUrl,
    fieldsFilledCount: fill.fieldsFilledCount,
    fieldsTotalCount: fill.fieldsTotalCount,
  };
}

function geotechFillToRequirement(reqId: string, fill: GeotechCoverageFill): RequirementAutoFill {
  return {
    requirementId: reqId,
    fields: stripUndefined(fill.fields),
    notes: fill.notes,
    confidence: fill.confidence,
    sourceProvider: fill.sourceProvider,
    sourceUrl: fill.sourceUrl,
    fieldsFilledCount: fill.fieldsFilledCount,
    fieldsTotalCount: fill.fieldsTotalCount,
  };
}

/**
 * Strip `undefined`-valued keys so the AuthorEvidenceDialog form-state
 * never receives a literal `undefined` (which React's controlled
 * inputs choke on — `value={undefined}` flips the input to
 * uncontrolled mid-life).
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' || typeof v === 'number') {
      out[k] = v;
    }
  }
  return out;
}
